// engine/audio/AudioManager.ts
import Phaser from "phaser";
import type { SoundConfig } from "../../types/config";
import { Sound } from "../entities/Sounds";

/**
 * Centralized audio control.
 * - Creates and owns all Sound entities
 * - Handles browser audio unlock once
 * - Exposes play/stop/fade, group ops, and full cleanup
 */
export class AudioManager {
    private adminMode: boolean;
    private scene: Phaser.Scene;
    private sounds = new Map<string, Sound>(); // key = SoundConfig.name
    private masterVolume = 1;
    private muted = false;

    // queue plays until first user gesture unlocks audio (once)
    private pendingPlays: string[] = [];
    private unlockBound = false;

    constructor(adminMode: boolean, scene: Phaser.Scene) {
        this.adminMode = adminMode;
        this.scene = scene;

        // Ensure we cleanup on scene end
        scene.events.once(Phaser.Scenes.Events.SHUTDOWN, () => this.destroy());
        scene.events.once(Phaser.Scenes.Events.DESTROY, () => this.destroy());
    }

    /** Register sounds from config; does NOT load files — assume preloaded by AssetManager. */
    public register(configs: SoundConfig[]) {
        for (const cfg of configs) {
            if (this.sounds.has(cfg.name)) continue;
            const s = new Sound(this.adminMode, cfg);
            this.sounds.set(cfg.name, s);
        }
    }

    /** Instantiate Phaser sounds for all registered entries. Call in/after `create()`. */
    public createAll() {
        for (const s of this.sounds.values()) {
            s.createPhaserSound(this.scene);
            // apply current master/mute
            this.applyMasterTo(s);
        }
    }

    // ──────────────────────── NEW PUBLIC HELPERS ────────────────────────

    /** Retourne l'entité Sound par son name (clé logique / Phaser key). */
    public getByName(name: string): Sound | undefined {
        return this.sounds.get(name);
    }

    /** Joue un son par son name (pratique pour les appels "fire-and-forget"). */
    public playByName(name: string): void {
        this.play(name);
    }

    /** Stoppe un son par son name. */
    public stopByName(name: string): void {
        this.stop(name);
    }

    /**
     * Crée l'instance Phaser pour un son **déjà enregistré**.
     * Utile après un lazy-load (ensureAudio) quand l'asset arrive en cache.
     */
    public createOne(name: string): void {
        const s = this.sounds.get(name);
        if (!s) {
            console.warn(`[AudioManager] createOne(): Unknown sound "${name}"`);
            return;
        }
        s.createPhaserSound(this.scene);
        this.applyMasterTo(s);
    }

    // ---------- Basic controls ----------

    public has(name: string) {
        return this.sounds.has(name);
    }

    public play(name: string) {
        const s = this.sounds.get(name);
        if (!s) {
            console.warn(`[AudioManager] Unknown sound "${name}"`);
            return;
        }

        // If audio is locked, queue once and install one unlock handler
        if (this.scene.sound?.locked) {
            this.pendingPlays.push(name);
            this.bindUnlockOnce();
            return;
        }

        if (this.muted) return; // muted = no-op play
        s.play();
    }

    public stop(name: string) {
        this.sounds.get(name)?.stop();
    }

    public restart(name: string) {
        const s = this.sounds.get(name);
        if (!s) return;
        if (this.muted) return; // stay silent if muted
        s.restart();
    }

    public fadeIn(name: string, ms = 300) {
        if (this.muted) return;
        this.sounds.get(name)?.fadeIn(ms);
    }

    public fadeOut(name: string, ms = 300) {
        this.sounds.get(name)?.fadeOut(ms);
    }

    // ---------- Bulk ops ----------

    public stopAll() {
        for (const s of this.sounds.values()) s.stop();
    }

    public fadeOutAll(ms = 300) {
        for (const s of this.sounds.values()) s.fadeOut(ms);
    }

    /** 0..1 — applies proportionally on top of each Sound’s own volume */
    public setMasterVolume(v: number) {
        this.masterVolume = Math.max(0, Math.min(1, v));
        for (const s of this.sounds.values()) this.applyMasterTo(s);
    }

    public getMasterVolume() {
        return this.masterVolume;
    }

    public muteAll() {
        this.muted = true;
        // quick silence without changing per-sound config
        for (const s of this.sounds.values()) s.setVolume(0);
    }

    public unmuteAll() {
        this.muted = false;
        for (const s of this.sounds.values()) this.applyMasterTo(s);
        // Optionally replay anything queued while muted — skipped by design
    }

    public isMuted() {
        return this.muted;
    }

    // ---------- Per-sound config passthrough ----------

    public setVolume(name: string, v: number) {
        const s = this.sounds.get(name);
        if (!s) return;
        const effective = this.muted ? 0 : Math.max(0, Math.min(1, v)) * this.masterVolume;
        s.setVolume(effective);
    }

    public setLoop(name: string, loop: boolean) {
        this.sounds.get(name)?.setLoop(loop);
    }

    // ---------- Cleanup ----------

    /** Full teardown; safe to call multiple times. */
    public destroy() {
        this.unbindUnlock();
        this.pendingPlays.length = 0;

        for (const s of this.sounds.values()) {
            try { s.destroy(); } catch { /* ignore */ }
        }
        this.sounds.clear();
    }

    // ---------- Internals ----------

    private applyMasterTo(s: Sound) {
        // We don’t know the sound’s “base” volume here; we just set what we have now.
        // If you want baseVolume * masterVolume behavior, add baseVolume to Sound and multiply here.
        const target = this.muted ? 0 : this.masterVolume;
        s.setVolume(target);
    }

    private bindUnlockOnce() {
        if (this.unlockBound) return;
        this.unlockBound = true;

        this.scene.input?.once(Phaser.Input.Events.POINTER_DOWN, () => {
            try { this.scene.sound?.unlock(); } catch { /* ignore */ }
            this.flushPendingPlays();
            this.unbindUnlock();
        });
    }

    private unbindUnlock() {
        if (!this.unlockBound) return;
        // input.once doesn’t need explicit off; we just mark the flag down.
        this.unlockBound = false;
    }

    private flushPendingPlays() {
        if (this.muted) { this.pendingPlays.length = 0; return; }
        const unique = Array.from(new Set(this.pendingPlays));
        this.pendingPlays.length = 0;
        for (const name of unique) this.sounds.get(name)?.play();
    }
}
